// Numbers and times the way an Indian trader reads them: rupees with lakh
// grouping, prices to the tick, times in IST whatever the browser's zone.
export const IST = "Asia/Kolkata";

const inr0 = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const inr2 = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num2 = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });

export function rupees(v: number | null | undefined, decimals: 0 | 2 = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "n/a";
  return (decimals === 2 ? inr2 : inr0).format(v);
}

/** Signed rupees for P&L: "+₹1,250" / "-₹830". */
export function pnl(v: number | null | undefined, decimals: 0 | 2 = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "n/a";
  const s = (decimals === 2 ? inr2 : inr0).format(Math.abs(v));
  return v > 0 ? `+${s}` : v < 0 ? `-${s}` : s;
}

export function price(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "n/a";
  return num2.format(v);
}

export function qty(v: number | null | undefined): string {
  if (v === null || v === undefined) return "n/a";
  return int.format(v);
}

export function big(v: number | null | undefined): string {
  if (v === null || v === undefined) return "n/a";
  return compact.format(v);
}

export function pct(v: number | null | undefined, digits = 1, signed = false): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "n/a";
  const s = `${Math.abs(v).toFixed(digits)}%`;
  if (!signed) return `${v < 0 ? "-" : ""}${s}`;
  return v > 0 ? `+${s}` : v < 0 ? `-${s}` : s;
}

export function tone(v: number | null | undefined): "up" | "down" | "flat" {
  if (!v) return "flat";
  return v > 0 ? "up" : "down";
}

const tFmt = new Intl.DateTimeFormat("en-IN", { timeZone: IST, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
const hmFmt = new Intl.DateTimeFormat("en-IN", { timeZone: IST, hour: "2-digit", minute: "2-digit", hour12: false });
const dFmt = new Intl.DateTimeFormat("en-IN", { timeZone: IST, day: "2-digit", month: "short", year: "numeric" });
const dShort = new Intl.DateTimeFormat("en-IN", { timeZone: IST, day: "2-digit", month: "short" });
const dtFmt = new Intl.DateTimeFormat("en-IN", { timeZone: IST, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });

const d = (v: string | number | Date) => (v instanceof Date ? v : new Date(v));
export const time = (v?: string | null) => (v ? tFmt.format(d(v)) : "n/a");
export const hm = (v?: string | null) => (v ? hmFmt.format(d(v)) : "n/a");
export const date = (v?: string | null) => (v ? dFmt.format(d(v)) : "n/a");
export const dayShort = (v?: string | null) => (v ? dShort.format(d(v)) : "n/a");
export const dateTime = (v?: string | null) => (v ? dtFmt.format(d(v)) : "n/a");

export function ago(v?: string | null): string {
  if (!v) return "never";
  const s = Math.max(0, (Date.now() - d(v).getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export function duration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

/** Bar start (ISO with offset) to the chart's time axis: seconds, shifted so the
 * chart (which renders UTC) shows Indian wall-clock time. */
export function chartTime(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 1000) + 19800;
}
